import { Button } from '@/components/ui/button'
import { canOpenPaymentModal } from '@/components/pos/posPayment'
import { getAssignedCustomer } from '@/components/pos/posCustomer'
import { PosCustomerCard, PosCustomerSearch } from '@/components/pos/PosCustomerSearch'
import { PosCustomerPackageBalances } from '@/components/pos/PosCustomerPackageBalances'
import { PosFloorPanel } from '@/components/pos/PosFloorPanel'
import { PosOperationsPanel } from '@/components/pos/PosOperationsPanel'

import { POS_PANEL_IDS } from './posViewport'

/**
 * @param {object} props
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {ReturnType<import('./posCart').toPosTotals>} props.totals
 * @param {import('@/types/api').SaleLine | null} [props.selectedLine]
 * @param {import('@/types/api').Stylist[]} props.stylists
 * @param {boolean} props.disabled
 * @param {boolean} [props.hasAddons]
 * @param {string} props.customerError
 * @param {string} props.holdError
 * @param {string} [props.floorQueueError]
 * @param {boolean} [props.canSendToFloor]
 * @param {() => void | Promise<void>} [props.onSendToFloor]
 * @param {(customer: import('@/types/api').Customer) => void} props.onSelectCustomer
 * @param {() => void} props.onClearCustomer
 * @param {() => void} [props.onOpenCustomerHistory]
 * @param {() => void} props.onOpenPayment
 * @param {string | number} [props.packageBalanceRefreshToken]
 * @param {(actionId: string) => void} props.onOperation
 * @param {() => void | Promise<void>} [props.onHoldTicket]
 */
export function PosCustomerPanel({
  sale,
  totals,
  selectedLine = null,
  stylists,
  disabled,
  hasAddons = false,
  customerError,
  holdError,
  floorQueueError = '',
  canSendToFloor = false,
  onSendToFloor,
  onSelectCustomer,
  onClearCustomer,
  onOpenCustomerHistory,
  onOpenPayment,
  packageBalanceRefreshToken = '',
  onOperation,
  onHoldTicket,
}) {
  const customer = getAssignedCustomer(sale)
  const canPay = canOpenPaymentModal(sale)

  return (
    <aside
      id={POS_PANEL_IDS.customer}
      className="pos-v2-payment pos-v2-right-panel flex min-h-0 flex-col overflow-hidden bg-white"
      aria-label="Customer, operations, and payment"
    >
      <div className="pos-v2-customer-zone shrink-0 border-b border-[#E5E7EB] p-1.5">
        {customer ? (
          <PosCustomerCard
            customer={customer}
            onClear={onClearCustomer}
            onOpenHistory={onOpenCustomerHistory}
            disabled={disabled}
          />
        ) : (
          <PosCustomerSearch
            onSelectCustomer={onSelectCustomer}
            disabled={disabled}
            error={customerError}
          />
        )}

        {customer ? (
          <div className="mt-1.5 space-y-1.5">
            <PosFloorPanel sale={sale} disabled={disabled} onHoldTicket={onHoldTicket} />
            <PosCustomerPackageBalances
              sale={sale}
              refreshToken={packageBalanceRefreshToken}
              disabled={disabled}
              compact
              onRedeem={() => onOperation('redeem')}
            />
          </div>
        ) : null}

        <div className="pos-v2-balance-strip mt-1.5 flex items-baseline justify-between gap-2">
          <span className="pos-v2-balance-label">Balance due</span>
          <span className="pos-v2-balance-amount font-mono">
            AED {totals.balance_due.toFixed(2)}
          </span>
        </div>

        {holdError ? (
          <p className="pos-v2-ui-meta mt-1 text-destructive">{holdError}</p>
        ) : null}
        {floorQueueError ? (
          <p className="pos-v2-ui-meta mt-1 text-destructive">{floorQueueError}</p>
        ) : null}
      </div>

      <div className="pos-v2-ops-scroll min-h-0 flex-1 overflow-y-auto">
        <PosOperationsPanel
          selectedLine={selectedLine}
          stylists={stylists}
          disabled={disabled}
          hasAddons={hasAddons}
          onAction={onOperation}
        />
      </div>

      <div className="shrink-0 border-t border-[#E5E7EB] bg-white p-1.5">
        <Button
          className="pos-v2-pay-btn h-12 w-full rounded-md font-bold"
          size="lg"
          disabled={disabled || !canPay}
          onClick={onOpenPayment}
        >
          Pay · AED {totals.balance_due.toFixed(2)}
        </Button>
      </div>
    </aside>
  )
}
